/* eslint-disable max-len */
import { Component, EventEmitter, HostListener, OnDestroy, OnInit, Output } from '@angular/core';
import { Store } from '@ngrx/store';
import { AppState } from 'src/app/store/app.reducer';
import { Observable, Subscription } from 'rxjs';
import { FormControl } from '@angular/forms';
import { PaginationParams } from 'src/app/model/api/pagination-params';
import { debounceTime, filter, map } from 'rxjs/operators';
import * as DeviceActions from 'src/app/store/device/device.actions';
import { MtmaAction } from 'src/app/model/mtma-action';
import { Command } from 'src/app/model/command';
import { TranslateService } from '@ngx-translate/core';
import { Site } from 'src/app/model/site';
import { KEYPADS_DEFAULT_SORT_COLUMN, KEYPADS_DEFAULT_SORT_COLUMN_DIRECTION, KEYPADS_PAGE_DEFAULT_SIZE, KEYPADS_PAGE_SIZE_OPTIONS, KEYPADS_TABLE_HEADER } from 'src/app/conf/mtma-keypads';
import { MtmaKeypad } from 'src/app/model/mtma-keypad';
import { DatePipe } from '@angular/common';
import { selectCurrentSite } from 'src/app/store/site/site.selector';
import { selectMtmaActions, selectMtmaActiveTask, selectMtmaCommandAutomation, selectMtmaKeypadModify, selectMtmaKeypads, selectMtmaKeypadsInvalid, selectMtmaKeypadsMaxData, selectMtmaKeypadsPagination } from 'src/app/store/device/device.selector';
import { selectCurrentLang } from 'src/app/store/user/user.selector';
import { ConfirmationService } from 'primeng/api';
import { DialogService, DynamicDialogRef } from 'primeng/dynamicdialog';
import { ItemsPagination, MtmaMaxData } from 'src/app/model/api/data-rest';
import { Lang } from 'src/app/model/lang';
import { MtmaTask } from 'src/app/model/mtma-task';
import { pageSortChange } from 'src/app/services/table';
import { TableLazyLoadEvent } from 'primeng/table';
import { MtmaKeypadDialogComponent } from '../mtma-manage-keypad-dialog/mtma-manage-keypad-dialog.component';
import { StandardMtmaLayout, StandardPageLayout } from 'src/app/modules/shared/stdlayoutlib/classes.stdlayoutlib';
import { selectDeviceTabIndex } from 'src/app/store/home/home.selector';
import { MtmaIndexTab } from 'src/app/model/device';

@Component({
	selector: 'app-mtma-manage-keypad',
	templateUrl: './mtma-manage-keypad.component.html',
	providers: [ConfirmationService, DialogService]
})
export class MtmaManageKeypadComponent implements OnInit, OnDestroy {
	// change activeIndex of device-tab-group for redirect into MTM Quick&Easy
	@Output() activeIndexChange = new EventEmitter<number>();

	site$: Observable<Site | undefined>;
	siteSub: Subscription | undefined;
	mtmaKeypads$: Observable<MtmaKeypad[] | undefined>;
	mtmaKeypadsSub: Subscription | undefined;
	mtmaKeypadsPagination$: Observable<ItemsPagination | undefined>;
	mtmaKeypadsPaginationSub: Subscription | undefined;
	mtmaKeypadsMaxData$: Observable<MtmaMaxData | undefined>;
	mtmaKeypadsMaxDataSub: Subscription | undefined;
	mtmaCommandAutomation$: Observable<Command[] | undefined>;
	mtmaCommandAutomationSub: Subscription | undefined;
	mtmaActions$: Observable<MtmaAction[] | undefined>;
	mtmaActionsSub: Subscription | undefined;
	mtmaKeypadModify$: Observable<boolean | undefined>;
	mtmaKeypadModifySub: Subscription | undefined;
	currentLang$: Observable<Lang> = new Observable();
	currentLangSub: Subscription | undefined;
	mtmaActiveTask$: Observable<MtmaTask | undefined>;
	mtmaActiveTaskSub: Subscription | undefined;
	mtmaKeypadsInvalid$: Observable<number | undefined>;
	mtmaKeypadsInvalidSub: Subscription | undefined;
	tabIndex: number;
	tabIndex$: Observable<number | undefined>;
	tabIndexSub: Subscription | undefined;

	searchFilter: FormControl = new FormControl('');
	filterSub: Subscription;
	filter: '';

	columns = KEYPADS_TABLE_HEADER;
	pagingSub: Subscription;
	pageSortSub: Subscription;
	paging: PaginationParams = {
		size: KEYPADS_PAGE_DEFAULT_SIZE,
		page: 1,
		sort: KEYPADS_DEFAULT_SORT_COLUMN,
		order: KEYPADS_DEFAULT_SORT_COLUMN_DIRECTION,
	};
	pageSize: number = KEYPADS_PAGE_DEFAULT_SIZE;
	pageSizeOptions = KEYPADS_PAGE_SIZE_OPTIONS;
	totalRecords: number;

	maxSizeMemorySub: Subscription;
	memorySize = 1;
	maxMemorySize = 1;

	keypadsLoading: boolean;
	mtmaKeypads: MtmaKeypad[];

	mtmaCommandAutomation?: Command[];

	invalidKeypadsSub: Subscription;
	invalidKeypads: number;

	currentSite: Site;


	mtmaActions: MtmaAction[] = [];
	mtmaActionsSuccessSub: Subscription;
	confirmDeleteSub: Subscription;

	isActiveTask = false;
	dateTransform: DatePipe;

	ref: DynamicDialogRef | undefined;

	acceptButtonStyle: string;
	rejectButtonStyle: string;

	// Design
	screenWidth: number;
	MIN_MD_WIDTH = 500;
	COLUMN_TITLE_MIN_WD = 647;
	MAX_MD_WIDTH = 1120;
	MIN_WIDTH_MX7 = 1700;
	LG_WIDTH = 1500;

	MAX_MD_WIDTH_SHORT = 858;
	constructor(
		//private dialog: MatDialog,
		private store: Store<AppState>,
		public confirmationService: ConfirmationService,
		private translate: TranslateService,
		public dialogService: DialogService,
	) {
		this.initSelectors();
	}

	initSelectors(): void {
		this.site$ = this.store.select(selectCurrentSite);
		this.mtmaKeypads$ = this.store.select(selectMtmaKeypads);
		this.mtmaKeypadsPagination$ = this.store.select(selectMtmaKeypadsPagination);
		this.mtmaKeypadsMaxData$ = this.store.select(selectMtmaKeypadsMaxData);
		this.mtmaCommandAutomation$ = this.store.select(selectMtmaCommandAutomation);
		this.mtmaKeypadsInvalid$ = this.store.select(selectMtmaKeypadsInvalid);
		this.mtmaActions$ = this.store.select(selectMtmaActions);
		this.mtmaKeypadModify$ = this.store.select(selectMtmaKeypadModify);
		this.currentLang$ = this.store.select(selectCurrentLang);
		this.mtmaActiveTask$ = this.store.select(selectMtmaActiveTask);
		this.tabIndex$ = this.store.select(selectDeviceTabIndex);
	}

	ngOnInit(): void {
		const curreLang = this.translate.currentLang;
		this.dateTransform = new DatePipe(curreLang);
		this.filterSub = this.searchFilter.valueChanges.pipe(
			debounceTime(500),
			map(v => v.trim().toLowerCase())
		).subscribe(value => {
			this.filter = value;
			this.dispatchGetKeypads();
		});
		this.dispatchGetKeypads();
		this.store.dispatch(DeviceActions.getMtmaActions());
		this.mtmaKeypadsSub = this.mtmaKeypads$.subscribe(mtmaKeypads => {
			this.mtmaKeypads = mtmaKeypads;
			this.keypadsLoading = false;
		});

		this.siteSub = this.site$.subscribe(currentSite => {
			this.currentSite = currentSite;
		});

		this.pagingSub = this.mtmaKeypadsPagination$
			.pipe(filter(p => !!p))
			.subscribe(paging => {
				this.totalRecords = paging.TotalCount;
			});

		this.maxSizeMemorySub = this.mtmaKeypadsMaxData$
			.pipe(filter(p => !!p))
			.subscribe(mtmaKeypadsMaxData => {
				this.memorySize = mtmaKeypadsMaxData.Current;
				this.maxMemorySize = mtmaKeypadsMaxData.Max;
			});

		this.invalidKeypadsSub = this.mtmaKeypadsInvalid$
			.subscribe(mtmaKeypadsInvalid => {
				this.invalidKeypads = mtmaKeypadsInvalid;
			});

		this.mtmaCommandAutomationSub = this.mtmaCommandAutomation$
			.subscribe(mtmaCommandAutomation => {
				this.mtmaCommandAutomation = mtmaCommandAutomation;
				this.createTranslationAction();
			});

		this.mtmaActionsSub = this.mtmaActions$
			.subscribe(mtmaActions => {
				this.mtmaActions = mtmaActions;
				this.getAutomationCommand();
				this.createTranslationAction();
			});

		this.currentLangSub = this.currentLang$.subscribe(() => {
			this.store.dispatch(DeviceActions.getMtmaActions());
		});

		this.mtmaKeypadModifySub = this.mtmaKeypadModify$
			.pipe(filter(mtmaKeypadModify => !!mtmaKeypadModify))
			.subscribe(() => {
				this.dispatchGetKeypads();
			});

		this.mtmaActiveTaskSub = this.mtmaActiveTask$.subscribe(mtmaActiveTask => {
			if (mtmaActiveTask.Id !== '') {
				this.isActiveTask = true;
			} else {
				this.isActiveTask = false;
			}
		});
		this.tabIndexSub = this.tabIndex$?.subscribe(
			async (tabIndex: number) => {
				this.tabIndex = tabIndex;
				this.dispatchGetKeypads();
			});

		this.onResize();
	}

	ngOnDestroy(): void {
		this.mtmaActionsSub?.unsubscribe();
		this.mtmaCommandAutomationSub?.unsubscribe();
		this.currentLangSub?.unsubscribe();
		this.mtmaKeypadModifySub?.unsubscribe();
		this.siteSub?.unsubscribe();
		this.maxSizeMemorySub?.unsubscribe();
		this.mtmaActiveTaskSub?.unsubscribe();
		this.invalidKeypadsSub?.unsubscribe();
		this.tabIndexSub?.unsubscribe();
	}

	loadKeypads(event: TableLazyLoadEvent) {
		this.keypadsLoading = true;

		this.paging = pageSortChange(this.paging, event);

		this.dispatchGetKeypads();
	}

	dispatchGetKeypads(): void {
		if (this.isSelectTab()) {
			this.store.dispatch(DeviceActions.getMtmaKeypads({
				filter: this.filter,
				sort: this.paging.sort, order: this.paging.order, size: this.paging.size, page: this.paging.page
			}));
		}
	}

	isSelectTab(): boolean {
		return this.tabIndex === MtmaIndexTab.MTM_KEYPADS;
	}

	addKeypad(): void {
		this.store.dispatch(DeviceActions.resetKeypadForm());
		this.ref = this.dialogService.open(MtmaKeypadDialogComponent, {
			header: this.translate.instant('ADD_KEYPAD'),
			width: '75vw',
			breakpoints: { '1600px': '98vw' },
			contentStyle: { overflow: 'auto' },
		});
	}

	editKeypad(keypad: MtmaKeypad): void {
		this.store.dispatch(DeviceActions.resetKeypadForm());
		this.ref = this.dialogService.open(MtmaKeypadDialogComponent, {
			header: this.translate.instant('EDIT_KEYPAD'),
			width: '75vw',
			breakpoints: { '1600px': '98vw' },
			contentStyle: { overflow: 'auto' },
			data: {
				keypad
			},
		});
	}

	createTranslationAction(): void {
		if (this.mtmaActions && this.mtmaCommandAutomation) {
			for (const action of this.mtmaActions) {
				if (action.AutomationId) {
					action.Traslation = this.getTranslationAction(action);
				} else {
					action.Traslation = this.translate.instant(action.Description);
				}
			}
		}
	}

	getTranslationAction(action: MtmaAction): string {
		for (const command of this.mtmaCommandAutomation) {
			let nameDevice = '';
			if (this.currentSite && this.currentSite.Devices) {
				for (const device of this.currentSite.Devices) {
					if (device.Id === command.AutomationId) {
						nameDevice = device.Name + ' - ';
					}
				}
			}
			if (action.AutomationId === command.AutomationId && action.Command === command.Id) {
				return nameDevice + command.Description;
			}
		}
		return '';
	}

	async getAutomationCommand(): Promise<void> {
		const mapAutomation = new Map<number, number>();
		for (const action of this.mtmaActions) {
			if (action.AutomationId && !mapAutomation.has(action.AutomationId)) {
				mapAutomation.set(action.AutomationId, action.AutomationId);
			}
		}
		let cont = 0;
		for (const key of mapAutomation.keys()) {
			setTimeout(() => {
				this.store.dispatch(DeviceActions.getMtmaAutomationCommands({ deviceId: key }));
			}, cont * 1000);
			cont++;
		}
	}

	getTextAction(keypad: MtmaKeypad): string {
		let text = '';
		if (keypad && keypad.Actions) {
			for (const action of keypad.Actions) {
				for (const actionAll of this.mtmaActions) {
					if (action === actionAll.Id) {
						if (text === '') {
							text = actionAll.Traslation;
						} else {
							text = text + ', ' + actionAll.Traslation;
						}
						break;
					}
				}
			}
		}
		return text;
	}

	delete(keypad: MtmaKeypad): void {
		/*const dialogRef = this.dialog.open(ConfirmationDialogComponent, {
			minWidth: '20vw',
			maxWidth: '50vw',
			data: { title: 'DELETE_KEYPAD', body: 'DELETE_KEYPAD_WARNING' }
		});
		this.confirmDeleteSub = dialogRef.afterClosed().subscribe(result => {
			if (result === 'success') {
				this.store.dispatch(DeviceActions.deleteMtmaKeypad({ keypadId:keypad.ObjId }));
			}
		});*/
		this.acceptButtonStyle = "p-button-danger";
		this.rejectButtonStyle = "p-button-secondary";
		this.confirmationService.confirm({
			header: this.translate.instant('DELETE_KEYPAD'),
			message: this.translate.instant('DELETE_KEYPAD_WARNING'),
			acceptLabel: this.translate.instant('CONFIRM'),
			rejectLabel: this.translate.instant('CANCEL'),
			icon: 'pi cm-alert',
			accept: () => {
				this.store.dispatch(DeviceActions.deleteMtmaKeypad({ keypadId: keypad.ObjId }));
			},
			reject: () => {
			}
		});
	}

	getRuleInfo(keypad: MtmaKeypad): string {
		let res = '';
		if (keypad.RuleId !== undefined) {
			res = this.translate.instant('GROUP') + ': ' + keypad.RuleName;
		}

		return res;
	}

	getRuleDates(keypad: MtmaKeypad): string {
		let res = '';
		if (keypad.DtFrom !== undefined && keypad.DtTo !== undefined) {
			res = res + ' ' + this.translate.instant('FROM') + ' ' + this.dateTransform.transform(keypad.DtFrom, 'dd/MM/yyyy HH:mm') +
				' - ' + this.translate.instant('TO') + ' ' + this.dateTransform.transform(keypad.DtTo, 'dd/MM/yyyy HH:mm');
		}
		return res;
	}

	getStyleKeypad(keypad: MtmaKeypad): string {
		if (keypad.Valid) {
			return '';
		}
		return 'color: red;';
	}

	getTooltipKeypad(keypad: MtmaKeypad): string {
		if (keypad.Valid) {
			return '';
		}
		return this.translate.instant('KEYPAD_INVALID');
	}

	getValueMemory(): number {
		return Math.ceil((this.memorySize / this.maxMemorySize) * 100);
	}

	goToMtmaQuickEasyTab() {
		this.activeIndexChange.emit(MtmaIndexTab.MTM_QUICKEASY);
	}

	// Design
	@HostListener('window:resize')
	onResize() {
		this.screenWidth = window.innerWidth;
	}

	onExtendedMobile() {
		return StandardPageLayout.onExtendedMobile(this.screenWidth);
	}

	actionLayout(position: string): string {
		return StandardMtmaLayout.actionLayout(this.screenWidth, position);
	}

	layoutMarginResponsive(): string {
		return StandardPageLayout.layoutMarginResponsive(this.screenWidth);
	}

	dynamicInputTextWidth(): string {
		return StandardPageLayout.dynamicInputTextWidth(this.screenWidth, 'w-25rem');
	}

	dynamicInputSearchWidth(): string {
		return StandardMtmaLayout.dynamicInputSearchWidth(this.screenWidth, 'w-25rem');
	}
}
